import type { StatusTone } from '@/lib/status-tones'
import { parseUTCDate } from '@/lib/utils'
import { shortDate } from '@/features/purchase-orders/action-card-model'
import type { OwedItem, OwedOverviewItem, OwedPlan, OwedScope } from './types'

export function isWaiting(item: Pick<OwedItem, 'status'>) {
  return item.status === 'waiting'
}

/** "Oct 11" for a stored timestamp, in the reader's time zone, with the year only when it isn't this year. */
export function shortDay(timestamp: string) {
  const date = parseUTCDate(timestamp)
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(date.getFullYear() !== new Date().getFullYear() ? { year: 'numeric' } : {}),
  })
}

/** "Garland · Prepaid invoice", or just the label when we don't know who owes it. */
export function owedTitle(item: Pick<OwedItem, 'party_name' | 'label' | 'kind'>) {
  const label = item.label || item.kind
  const party = item.party_name?.trim()
  return party ? `${party} · ${label}` : label
}

/** The row's status pill: late, due, received or not needed. */
export function owedStatusPill(item: OwedItem): { tone: StatusTone; text: string } {
  if (item.status === 'received') {
    return { tone: 'success', text: item.settled_at ? `Received ${shortDay(item.settled_at)}` : 'Received' }
  }
  if (item.status === 'waived') return { tone: 'neutral', text: 'Not needed' }
  if (item.status === 'cancelled') return { tone: 'neutral', text: 'Cancelled' }
  if (item.days_late > 0) {
    return { tone: 'danger', text: `Due ${shortDate(item.due_on)} · ${item.days_late}d late` }
  }
  return { tone: 'warning', text: `Due ${shortDate(item.due_on)}` }
}

/** How many are still waiting, and how many of those are late. */
export function waitingCounts(items: readonly OwedItem[]) {
  const waiting = items.filter(isWaiting)
  return { waiting: waiting.length, late: waiting.filter((item) => item.days_late > 0).length }
}

function isoDay(date: Date) {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

/** YYYY-MM-DD for today in the reader's time zone. */
export function todayIso(now: Date = new Date()) {
  return isoDay(now)
}

/** YYYY-MM-DD ``days`` weekdays after ``from`` (Saturdays and Sundays skipped). */
export function addBusinessDays(days: number, from: Date = new Date()) {
  const date = new Date(from.getFullYear(), from.getMonth(), from.getDate())
  let left = days
  while (left > 0) {
    date.setDate(date.getDate() + 1)
    const weekday = date.getDay()
    if (weekday !== 0 && weekday !== 6) left -= 1
  }
  return isoDay(date)
}

/** The query that lists a record's owed items. */
export function scopeQuery(scope: OwedScope): Record<string, string> {
  if (scope.kind === 'purchase_order') return { purchase_order_uuid: scope.poUuid }
  if (scope.kind === 'ap_invoice') return { ap_invoice_uuid: scope.invoiceUuid }
  return { customer_thread_uuid: scope.threadUuid }
}

/** Customer threads offer what a customer owes; POs and invoices what suppliers and carriers owe. */
export function kindFitsScope(owedBy: string, scope: OwedScope) {
  return scope.kind === 'customer_thread' ? owedBy === 'customer' : owedBy !== 'customer'
}

/** Waits another check already chases: say which, so a late one doesn't read as forgotten. */
const FLOW_NOTES: Record<string, string> = {
  supplier_follow_up: 'Chased by the PO follow-up emails',
  spec_credit: 'Chased by the spec credit deadline',
  pickup_checks: 'Chased by the pickup checks',
  sales_tax_requests: 'Chased by the sales tax request',
  invoice_reminders: 'Chased by the invoice reminders',
  finished_po: 'Not chased: the PO is finished or old',
  finished_invoice: 'Not chased: the invoice is paid, closed or old',
}

const ROLE_NAMES: Record<string, string> = { ops: 'Ops', accounting: 'Accounting', sales: 'Sales' }

/** Kinds that are listed but never chased, and why. */
const NOT_CHASED_NOTES: Record<string, string> = { invoice: 'Not chased: suppliers bill on their own schedule' }

/** What happens next, in one line: "Reminded Oct 28 · next reminder Nov 2 · Ops calls Nov 5" (USA-1444). */
export function planText(plan: OwedPlan | null | undefined): string | null {
  if (!plan || plan.listed_only) return null
  const who = ROLE_NAMES[plan.staff_role] ?? 'Staff'
  if (plan.paused) return 'Paused while the PO is on hold or has an open issue'
  if (plan.with_staff) return plan.staff_calls ? `${who} is calling them` : `${who} is following up`
  const steps: string[] = []
  if (plan.reminded_on) steps.push(`Reminded ${shortDate(plan.reminded_on)}`)
  if (plan.next_reminder_on) {
    steps.push(`${plan.reminded_on ? 'next reminder' : 'Reminder'} ${shortDate(plan.next_reminder_on)}`)
  }
  if (plan.staff_on) steps.push(`${who} ${plan.staff_calls ? 'calls' : 'follows up'} ${shortDate(plan.staff_on)}`)
  return steps.length > 0 ? steps.join(' · ') : null
}

/** A waiting item's next step: its plan, or the other check that chases it; nothing once it is settled. */
export function nextStepText(item: Pick<OwedItem, 'status' | 'plan' | 'handled_by_flow' | 'kind'>): string | null {
  if (!isWaiting(item)) return null
  if (item.plan?.listed_only) return NOT_CHASED_NOTES[item.kind] ?? 'Not chased'
  return planText(item.plan) ?? (item.handled_by_flow ? (FLOW_NOTES[item.handled_by_flow] ?? null) : null)
}

export interface PartyGroup {
  party: string
  items: OwedOverviewItem[]
}

/** Waits nobody chases (finished or old POs and invoices, terms invoices) come after the ones being chased. */
function notChased(item: OwedOverviewItem) {
  return (
    item.handled_by_flow === 'finished_po' ||
    item.handled_by_flow === 'finished_invoice' ||
    Boolean(item.plan?.listed_only)
  )
}

function byUrgency(a: OwedOverviewItem, b: OwedOverviewItem) {
  return Number(notChased(a)) - Number(notChased(b)) || a.due_on.localeCompare(b.due_on)
}

/** What each supplier, carrier or customer owes us, soonest due first; whoever owes the oldest item leads. */
export function groupByParty(items: readonly OwedOverviewItem[]): PartyGroup[] {
  const groups = new Map<string, OwedOverviewItem[]>()
  for (const item of items) {
    const party = item.party_name?.trim() || 'Not named'
    groups.set(party, [...(groups.get(party) ?? []), item])
  }
  return [...groups.entries()]
    .map(([party, rows]) => ({ party, items: [...rows].sort(byUrgency) }))
    .sort((a, b) => {
      const [first, second] = [a.items[0], b.items[0]]
      return (first && second ? byUrgency(first, second) : 0) || a.party.localeCompare(b.party)
    })
}

/** Search across who owes it, what, and the PO number. */
export function matchesSearch(item: OwedOverviewItem, search: string) {
  const needle = search.trim().toLowerCase()
  if (!needle) return true
  return [item.party_name, item.summary, item.label, item.purchase_order?.po_number].some((value) =>
    value?.toLowerCase().includes(needle),
  )
}
